<template>
  <div class="px-4 lg:px-0">
    <div class="w-full lg:max-w-content-xs bg-grey-lightest rounded-lg mb-5">
      <div
        class="relative h-15 w-full p-5 flex items-center expand-button cursor-pointer"
        ref="expandButton"
        @click.prevent="toggleForm"
      >
        <h2
          class="font-primary font-extrabold text-blue-brand text-20 lg:text-26 leading-1 -tracking-1/2"
        >
          {{ isBooker ? 'Your Details (Booker)' : `Additional Guest ${guestNumber}` }}
        </h2>
        <div
          v-if="!isComplete && unfilledFields > 0"
          class="absolute right-15 h-7.5 w-7.5 rounded-full bg-yellow-brand flex items-center justify-center"
        >
          <p class="font-primary font-extrabold text-14">{{ unfilledFields }}</p>
        </div>
        <div class="absolute top-0 right-0 h-15 w-15 flex items-center justify-center">
          <span class="text-blue-dark triangle triangle-md expand-triangle"></span>
        </div>
      </div>

      <form @submit.prevent="onSubmit" class="expand-list print-force-height-auto" ref="expandForm">
        <div class="flex flex-col md:flex-row my-5 px-5">
          <div class="relative w-full md:w-1/2 md:pr-2.5 mb-5 md:mb-0">
            <span
              v-if="!workingGuest.real_name && !isComplete"
              class="absolute top-px mt-px -left-3 h-2.5 w-2.5 rounded-full bg-yellow-brand"
            ></span>
            <label for="guestName" class="relative label block mb-2"
              >Full Name (As shown in passport)</label
            >
            <template v-if="!isComplete">
              <input
                type="text"
                name="guestName"
                class="input block w-full"
                placeholder="Example name"
                @change="onChange"
                v-model="workingGuest.real_name"
              />
            </template>
            <template v-else>
              <p class="text-20 leading-1.6">{{ workingGuest.real_name }}</p>
            </template>
          </div>
          <div class="relative w-full md:w-1/2 md:pl-2.5">
            <span
              v-if="!workingGuest.dob && !isComplete"
              class="absolute top-px mt-px -left-3 h-2.5 w-2.5 rounded-full bg-yellow-brand"
            ></span>
            <label for="guestDateOfBirth" class="relative label block mb-2">Date of Birth</label>
            <template v-if="!isComplete">
              <datepicker
                class="input block w-full"
                name="guestDateOfBirth"
                placeholder="DD/MM/YYY"
                v-model="workingGuest.dob"
                @selected="updateDOB"
              ></datepicker>
            </template>
            <template v-else>
              <datepicker
                class="input input-readonly block w-full bg-grey-lightest text-20 p-0"
                :disabled="true"
                placeholder="DD/MM/YYY"
                format="dd/MM/yyyy"
                v-model="workingGuest.dob"
              ></datepicker>
            </template>
          </div>
        </div>

        <p v-if="!isComplete" class="relative w-full text-16 leading-1.63 my-5 px-5">
          <span
            v-if="!workingGuest.phone && !workingGuest.email && !isComplete"
            class="absolute top-2 left-2 h-2.5 w-2.5 rounded-full bg-yellow-brand"
          ></span>
          We require at least one up to date method to contact you, should we need to discuss
          anything.
        </p>

        <div class="flex flex-col md:flex-row my-5 px-5">
          <div class="relative w-full md:w-1/2 md:pr-2.5 mb-5 md:mb-0">
            <label for="guestPhone" class="label block mb-2">Contact Number</label>
            <template v-if="!isComplete">
              <div class="relative">
                <phone-mask-input
                  v-model="workingGuest.phone"
                  autoDetectCountry
                  @onValidate="onUpdatePhoneNumber"
                  inputClass="input block w-full"
                ></phone-mask-input>
              </div>
            </template>
            <template v-else>
              <phone-mask-input
                v-if="workingGuest.phone"
                v-model="workingGuest.phone"
                autoDetectCountry
                :disabled="true"
                inputClass="input input-readonly block w-full bg-grey-lightest text-20 p-0"
              ></phone-mask-input>
              <p v-else class="text-20 leading-1.6">N/A</p>
            </template>
          </div>
          <div class="relative w-full md:w-1/2 md:pl-2.5">
            <label for="preferredContact" class="label block mb-2">Preferred</label>
            <template v-if="!isComplete">
              <div class="relative inline-block">
                <input
                  type="radio"
                  name="preferredContact"
                  value="phone"
                  v-model="workingGuest.preferred_contact_method"
                  class="h-5 w-5 rounded align-middle"
                />
                <span class="radio-dot"></span>
              </div>
            </template>
            <template v-else>
              <div v-if="workingGuest.preferred_contact_method === 'phone'" class="pt-1">
                <div class="flex items-center justify-center h-5 w-5 bg-yellow-brand rounded-full">
                  <div class="h-2 w-2 bg-blue-dark rounded-full"></div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <div class="flex flex-col md:flex-row my-5 px-5">
          <div class="relative w-full md:w-1/2 md:pr-2.5 mb-5 md:mb-0">
            <label for="guestEmail" class="label block mb-2">Contact Email</label>
            <template v-if="!isComplete">
              <div class="relative">
                <input
                  type="email"
                  name="guestEmail"
                  class="input block w-full"
                  placeholder="name@email.com"
                  @change="onChange"
                  v-model="workingGuest.email"
                />
              </div>
            </template>
            <template v-else>
              <p class="text-20 leading-1.6">
                {{ workingGuest.email ? workingGuest.email : 'N/A' }}
              </p>
            </template>
          </div>
          <div class="relative w-full md:w-1/2 md:pl-2.5">
            <label for="" class="label block md:hidden mb-2 h-3.5">Preferred</label>
            <label for="" class="label hidden md:block mb-2 h-3.5"></label>
            <template v-if="!isComplete">
              <div class="relative inline-block">
                <input
                  type="radio"
                  :class="isComplete ? '' : 'cursor-pointer'"
                  name="preferredContact"
                  value="email"
                  v-model="workingGuest.preferred_contact_method"
                  :disabled="isComplete"
                  class="h-5 w-5 rounded align-middle"
                />
                <span class="radio-dot"></span>
              </div>
            </template>
            <template v-else>
              <div v-if="workingGuest.preferred_contact_method === 'email'" class="pt-1">
                <div class="flex items-center justify-center h-5 w-5 bg-yellow-brand rounded-full">
                  <div class="h-2 w-2 bg-blue-dark rounded-full"></div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <template v-if="!isBooker && !isComplete">
          <h2
            class="font-primary font-extrabold text-blue-brand text-26 leading-1 -tracking-1/2 mx-5 pt-5 mb-5 border-t border-black-0.1"
          >
            Address
          </h2>

          <div class="relative w-full my-5 px-5 flex items-center">
            <label class="block h-5 w-5 relative cursor-pointer">
              <input
                type="checkbox"
                class="custom-checkbox h-5 w-5 bg-white rounded checked:bg-yellow-brand cursor-pointer"
                @click.stop="toggleSameAddressAsBooker($event)"
              />
              <svg-sprite
                href="icon-tick"
                class="checkbox-tick h-4 w-4 absolute pin-c pointer-events-none"
              ></svg-sprite>
            </label>
            <span class="pl-2.5 cursor-pointer">Same Details as Booker</span>
          </div>
        </template>

        <div class="flex flex-col md:flex-row my-5 px-5">
          <div class="relative w-full md:w-1/2 md:pr-2.5 mb-5 md:mb-0">
            <span
              v-if="!workingGuest.address && !isComplete"
              class="absolute top-px mt-px -left-3 h-2.5 w-2.5 rounded-full bg-yellow-brand"
            ></span>
            <label for="guestAddress" class="relative label block mb-2">Address</label>
            <template v-if="!isComplete">
              <input
                type="text"
                name="guestAddress"
                class="input block w-full"
                placeholder="123 Road Name, Town, City"
                @change="onChange"
                v-model="workingGuest.address"
              />
            </template>
            <template v-else>
              <p class="text-20 leading-1.6">{{ workingGuest.address }}</p>
            </template>
          </div>
          <div class="relative w-full md:w-1/2 md:pl-2.5">
            <span
              v-if="!workingGuest.postcode && !isComplete"
              class="absolute top-px mt-px -left-3 h-2.5 w-2.5 rounded-full bg-yellow-brand"
            ></span>
            <label for="guestPostcode" class="label block mb-2">Postcode / Zip Code</label>
            <template v-if="!isComplete">
              <input
                type="text"
                name="guestPostcode"
                class="input block w-full"
                placeholder="AB12 3CD"
                @change="onChange"
                v-model="workingGuest.postcode"
              />
            </template>
            <template v-else>
              <p class="text-20 leading-1.6">{{ workingGuest.postcode }}</p>
            </template>
          </div>
        </div>

        <div class="flex flex-col md:flex-row my-5 px-5">
          <div class="relative w-full md:w-1/2 md:pr-2.5 mb-5 md:mb-0">
            <span
              v-if="!workingGuest.country && !isComplete"
              class="absolute top-px mt-px -left-3 h-2.5 w-2.5 rounded-full bg-yellow-brand"
            ></span>
            <label for="guestCountry" class="label block mb-2">Country</label>
            <template v-if="!isComplete">
              <input
                type="text"
                name="guestCountry"
                class="input block w-full"
                placeholder="Country"
                @change="onChange"
                v-model="workingGuest.country"
              />
            </template>
            <template v-else>
              <p class="text-20 leading-1.6">{{ workingGuest.country }}</p>
            </template>
          </div>
          <div class="relative w-full md:w-1/2 md:pl-2.5"></div>
        </div>

        <h2
          class="font-primary font-extrabold text-blue-brand text-26 leading-1 -tracking-1/2 mx-5 pt-5 mb-5 border-t border-black-0.1"
        >
          Passport
        </h2>

        <div class="flex flex-col md:flex-row my-5 px-5">
          <div class="relative w-full md:w-1/2 md:pr-2.5 mb-5 md:mb-0">
            <span
              v-if="!workingGuest.passport_type && !isComplete"
              class="absolute top-px mt-px -left-3 h-2.5 w-2.5 rounded-full bg-yellow-brand"
            ></span>
            <label for="guestPassportType" class="label block mb-2">Passport Type</label>
            <template v-if="!isComplete">
              <div class="select-wrapper relative text-blue-dark">
                <select
                  name="guestPassportType"
                  class="input block w-full"
                  @change="onChange"
                  v-model="workingGuest.passport_type"
                >
                  <option v-for="(countryName, key) in passportTypes" :key="key" :value="key">{{
                    countryName
                  }}</option>
                </select>
              </div>
            </template>
            <template v-else>
              <p class="text-20 leading-1.6">{{ workingGuest.passport_type }}</p>
            </template>
          </div>
          <div class="relative w-full md:w-1/2 md:pl-2.5">
            <span
              v-if="!workingGuest.passport_number && !isComplete"
              class="absolute top-px mt-px -left-3 h-2.5 w-2.5 rounded-full bg-yellow-brand"
            ></span>
            <label for="guestPassportNumber" class="label block mb-2">Passport Number</label>
            <template v-if="!isComplete">
              <input
                type="text"
                required
                name="guestPassportNumber"
                class="input block w-full"
                placeholder="Passport number..."
                @change="onChange"
                v-model="workingGuest.passport_number"
              />
            </template>
            <template v-else>
              <p class="text-20 leading-1.6">{{ workingGuest.passport_number }}</p>
            </template>
          </div>
        </div>

        <div class="flex flex-col md:flex-row my-5 px-5">
          <div class="relative w-full md:w-1/2 md:pr-2.5 mb-5 md:mb-0">
            <span
              v-if="!workingGuest.passport_expires && !isComplete"
              class="absolute top-px mt-px -left-3 h-2.5 w-2.5 rounded-full bg-yellow-brand"
            ></span>
            <label for="guestPassportExpiry" class="label block mb-2">Expiry Date</label>
            <template v-if="!isComplete">
              <datepicker
                name="guestPassportExpiry"
                class="input block w-full"
                placeholder="MM/YYYY"
                :minimumView="'month'"
                :maximumView="'year'"
                :initialView="'year'"
                format="MM/yyyy"
                @selected="updateExpiry"
                v-model="workingGuest.passport_expires"
              ></datepicker>
            </template>
            <template v-else>
              <datepicker
                class="input input-readonly block w-full bg-grey-lightest text-20 p-0"
                :disabled="true"
                placeholder="MM/YYY"
                format="MM/yyyy"
                v-model="workingGuest.passport_expires"
              ></datepicker>
            </template>
          </div>
          <div class="relative w-full md:w-1/2 md:pl-2.5"></div>
        </div>

        <h2
          class="font-primary font-extrabold text-blue-brand text-26 leading-1 -tracking-1/2 mx-5 pt-5 mb-5 border-t border-black-0.1"
        >
          Insurance
        </h2>

        <div v-if="!isBooker && !isComplete" class="w-full my-5 px-5 flex items-center">
          <label class="block h-5 w-5 relative cursor-pointer">
            <input
              type="checkbox"
              class="custom-checkbox h-5 w-5 bg-white rounded checked:bg-yellow-brand cursor-pointer"
              @click.stop="toggleSameInsuranceAsBooker($event)"
            />
            <svg-sprite
              href="icon-tick"
              class="checkbox-tick h-4 w-4 absolute pin-c pointer-events-none"
            ></svg-sprite>
          </label>
          <span class="pl-2.5 cursor-pointer">Same Details as Booker</span>
        </div>

        <div class="flex flex-col md:flex-row my-5 px-5">
          <div class="relative w-full md:w-1/2 md:pr-2.5 mb-5 md:mb-0">
            <span
              v-if="!workingGuest.insurance_provider && !isComplete"
              class="absolute top-px mt-px -left-3 h-2.5 w-2.5 rounded-full bg-yellow-brand"
            ></span>
            <label for="guestInsuranceProvider" class="label block mb-2">Insurance Provider</label>
            <template v-if="!isComplete">
              <input
                type="text"
                required
                name="guestInsuranceProvider"
                class="input block w-full"
                placeholder="Company name..."
                @change="onChange"
                v-model="workingGuest.insurance_provider"
              />
            </template>
            <template v-else>
              <p class="text-20 leading-1.6">{{ workingGuest.insurance_provider }}</p>
            </template>
          </div>
          <div class="relative w-full md:w-1/2 md:pl-2.5">
            <span
              v-if="!workingGuest.insurance_policy_number && !isComplete"
              class="absolute top-px mt-px -left-3 h-2.5 w-2.5 rounded-full bg-yellow-brand"
            ></span>
            <label for="guestInsurancePolicyNumber" class="label block mb-2">Policy Number</label>
            <template v-if="!isComplete">
              <input
                type="text"
                required
                name="guestInsurancePolicyNumber"
                class="input block w-full"
                placeholder="Policy number..."
                @change="onChange"
                v-model="workingGuest.insurance_policy_number"
              />
            </template>
            <template v-else>
              <p class="text-20 leading-1.6">{{ workingGuest.insurance_policy_number }}</p>
            </template>
          </div>
        </div>

        <div class="flex flex-col md:flex-row my-5 px-5">
          <div class="relative w-full md:w-1/2 md:pr-2.5 mb-5 md:mb-0">
            <span
              v-if="!workingGuest.insurance_contact_number && !isComplete"
              class="absolute top-px mt-px -left-3 h-2.5 w-2.5 rounded-full bg-yellow-brand"
            ></span>
            <label for="guestInsuranceContactNumber" class="label block mb-2"
              >24 Hour Contact Number</label
            >
            <template v-if="!isComplete">
              <div class="relative">
                <phone-mask-input
                  v-model="workingGuest.insurance_contact_number"
                  autoDetectCountry
                  @onValidate="onUpdateInsurancePhoneNumber"
                  inputClass="input block w-full"
                ></phone-mask-input>
              </div>
            </template>
            <template v-else>
              <phone-mask-input
                v-if="workingGuest.insurance_contact_number"
                v-model="workingGuest.insurance_contact_number"
                autoDetectCountry
                :disabled="true"
                inputClass="input input-readonly block w-full bg-grey-lightest text-20 p-0"
              ></phone-mask-input>
              <p v-else class="text-20 leading-1.6">N/A</p>
            </template>
          </div>
          <div class="relative w-full md:w-1/2 md:pl-2.5"></div>
        </div>

        <h2
          class="realative w-full font-primary font-extrabold text-blue-brand text-26 leading-1 -tracking-1/2 mx-5 pt-5 mb-5 border-t border-black-0.1"
        >
          Next of Kin
        </h2>

        <div v-if="!isBooker && !isComplete" class="w-full my-5 px-5 flex items-center">
          <label class="block h-5 w-5 relative cursor-pointer">
            <input
              type="checkbox"
              class="custom-checkbox h-5 w-5 bg-white rounded checked:bg-yellow-brand cursor-pointer"
              @click.stop="toggleSameNextOfKinAsBooker($event)"
            />
            <svg-sprite
              href="icon-tick"
              class="checkbox-tick h-4 w-4 absolute pin-c pointer-events-none"
            ></svg-sprite>
          </label>
          <span class="pl-2.5 cursor-pointer">Same Details as Booker</span>
        </div>

        <div class="flex flex-col md:flex-row my-5 px-5">
          <div class="relative w-full md:w-1/2 md:pr-2.5 mb-5 md:mb-0">
            <span
              v-if="!workingGuest.next_of_kin_name && !isComplete"
              class="absolute top-px mt-px -left-3 h-2.5 w-2.5 rounded-full bg-yellow-brand"
            ></span>
            <label for="guestNextfKinName" class="relative label block mb-2">Full Name</label>
            <template v-if="!isComplete">
              <input
                type="text"
                name="guestNextfKinName"
                class="input block w-full"
                placeholder="Full name"
                @change="onChange"
                v-model="workingGuest.next_of_kin_name"
              />
            </template>
            <template v-else>
              <p class="text-20 leading-1.6">{{ workingGuest.next_of_kin_name }}</p>
            </template>
          </div>
          <div class="relative w-full md:w-1/2 md:pl-2.5"></div>
        </div>

        <p v-if="!isComplete" class="relative w-full text-16 leading-1.63 my-5 px-5">
          <span
            v-if="!workingGuest.next_of_kin_phone && !workingGuest.next_of_kin_email"
            class="absolute top-2 left-2 h-2.5 w-2.5 rounded-full bg-yellow-brand"
          ></span>
          We require at least one method to contact for next of kin.
        </p>

        <div class="flex flex-col md:flex-row my-5 px-5">
          <div class="relative w-full md:w-1/2 md:pr-2.5 mb-5 md:mb-0">
            <label for="guestNextOfKinPhone" class="label block mb-2">Contact Number</label>
            <template v-if="!isComplete">
              <div class="relative">
                <phone-mask-input
                  v-model="workingGuest.next_of_kin_phone"
                  autoDetectCountry
                  @onValidate="onUpdateNextOfKinPhoneNumber"
                  inputClass="input block w-full"
                ></phone-mask-input>
              </div>
            </template>
            <template v-else>
              <phone-mask-input
                v-if="workingGuest.next_of_kin_phone"
                v-model="workingGuest.next_of_kin_phone"
                autoDetectCountry
                :disabled="true"
                inputClass="input input-readonly block w-full bg-grey-lightest text-20 p-0"
              ></phone-mask-input>
              <p v-else class="text-20 leading-1.6">N/A</p>
            </template>
          </div>
          <div class="relative w-full md:w-1/2 md:pl-2.5">
            <label for="nextOfKinPreferredContact" class="label block mb-2">Preferred</label>
            <template v-if="!isComplete">
              <div class="relative inline-block">
                <input
                  type="radio"
                  :class="isComplete ? '' : 'cursor-pointer'"
                  name="nextOfKinPreferredContact"
                  value="phone"
                  v-model="workingGuest.next_of_kin_preferred_contact"
                  :disabled="isComplete"
                  class="h-5 w-5 rounded align-middle"
                />
                <span class="radio-dot"></span>
              </div>
            </template>
            <template v-else>
              <div v-if="workingGuest.next_of_kin_preferred_contact === 'phone'" class="pt-1">
                <div class="flex items-center justify-center h-5 w-5 bg-yellow-brand rounded-full">
                  <div class="h-2 w-2 bg-blue-dark rounded-full"></div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <div class="flex flex-col md:flex-row my-5 px-5">
          <div class="relative w-full md:w-1/2 md:pr-2.5 mb-5 md:mb-0">
            <label for="guestNextOfKinEmail" class="label block mb-2">Contact Email</label>
            <template v-if="!isComplete">
              <div class="relative">
                <input
                  type="email"
                  name="guestNextOfKinEmail"
                  class="input block w-full"
                  placeholder="name@email.com"
                  @change="onChange"
                  v-model="workingGuest.next_of_kin_email"
                />
              </div>
            </template>
            <template v-else>
              <p class="text-20 leading-1.6">
                {{ workingGuest.next_of_kin_email ? workingGuest.next_of_kin_email : 'N/A' }}
              </p>
            </template>
          </div>
          <div class="relative w-full md:w-1/2 md:pl-2.5">
            <label for="" class="label block md:hidden mb-2 h-3.5">Preferred</label>
            <label for="" class="label hidden md:block mb-2 h-3.5"></label>
            <template v-if="!isComplete">
              <div class="relative inline-block">
                <input
                  type="radio"
                  :class="isComplete ? '' : 'cursor-pointer'"
                  name="nextOfKinPreferredContact"
                  value="email"
                  v-model="workingGuest.next_of_kin_preferred_contact"
                  :disabled="isComplete"
                  class="h-5 w-5 rounded align-middle"
                />
                <span class="radio-dot"></span>
              </div>
            </template>
            <template v-else>
              <div v-if="workingGuest.next_of_kin_preferred_contact === 'email'" class="pt-1">
                <div class="flex items-center justify-center h-5 w-5 bg-yellow-brand rounded-full">
                  <div class="h-2 w-2 bg-blue-dark rounded-full"></div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <h2
          class="font-primary font-extrabold text-blue-brand text-26 leading-1 -tracking-1/2 mx-5 pt-5 mb-5 border-t border-black-0.1"
        >
          Optional Extras &amp; Add on Adventures
        </h2>

        <template v-if="!isComplete">
          <!-- extras -->
          <template v-if="extras.length > 0">
            <label
              :for="`extra-${extra.extra.uuid}-${guest.uuid}`"
              v-for="(extra, index) in extras"
              :key="`extra-${index}`"
              class="relative w-full my-5 px-7.5 flex items-center"
            >
              <span
                v-if="
                  guestsAddons.length < 1 &&
                    guestsExtras.length < 1 &&
                    guestsAddonExtras.length < 1 &&
                    !isComplete
                "
                class="absolute -top-2 left-2 h-2.5 w-2.5 rounded-full bg-yellow-brand"
              ></span>
              <span class="block h-5 w-5 relative cursor-pointer">
                <input
                  :id="`extra-${extra.extra.uuid}-${guest.uuid}`"
                  type="checkbox"
                  class="custom-checkbox h-5 w-5 bg-white rounded checked:bg-yellow-brand cursor-pointer"
                  :checked="extraIsChecked(extra.extra.uuid)"
                  @change="toggleExtraChecked($event, extra)"
                  :disabled="
                    !extraIsChecked(extra.extra.uuid) &&
                      availableExtras[extra.extra.uuid] <= assignedExtras[extra.extra.uuid]
                  "
                />
                <svg-sprite
                  href="icon-tick"
                  class="checkbox-tick h-4 w-4 absolute pin-c pointer-events-none"
                ></svg-sprite>
              </span>
              <span
                class="pl-2.5 flex flex-col cursor-pointer"
                :class="
                  !extraIsChecked(extra.extra.uuid) &&
                  availableExtras[extra.extra.uuid] <= assignedExtras[extra.extra.uuid]
                    ? 'opacity-50'
                    : ''
                "
              >
                <span class="font-primary font-extrabold text-blue-brand text-16 tracking-1/4">{{
                  extra.extra.name
                }}</span>
                <span class="font-primary font-normal text-blue-brand text-16 tracking-1/4"
                  >{{ readableDate(extra.start_date) }} - {{ readableDate(extra.end_date) }}</span
                >
              </span>
            </label>
          </template>
          <template v-else>
            <p class="text-20 leading-1.6 my-5 px-5">You have no extras for this adventure.</p>
          </template>
          <!-- addons -->
          <template v-if="addons.length > 0">
            <div v-for="(addon, index) in addons" :key="`addon-${index}`">
              <label
                class="w-full my-5 px-7.5 flex items-center"
                :for="`extra-${addon.addon.slug}-${guest.uuid}`"
              >
                <span class="block h-5 w-5 relative cursor-pointer">
                  <input
                    type="checkbox"
                    class="custom-checkbox h-5 w-5 bg-white rounded checked:bg-yellow-brand cursor-pointer"
                    :id="`extra-${addon.addon.slug}-${guest.uuid}`"
                    :checked="addonIsChecked(addon.addon.slug)"
                    @change="toggleAddonChecked($event, addon)"
                    :disabled="
                      !addonIsChecked(addon.addon.slug) &&
                        availableAddons[addon.addon.slug] <= assignedAddons[addon.addon.slug]
                    "
                  />
                  <svg-sprite
                    href="icon-tick"
                    class="checkbox-tick h-4 w-4 absolute pin-c pointer-events-none"
                  ></svg-sprite>
                </span>
                <span
                  class="pl-2.5 flex flex-col cursor-pointer"
                  :class="
                    !addonIsChecked(addon.addon.slug) &&
                    availableAddons[addon.addon.slug] <= assignedAddons[addon.addon.slug]
                      ? 'opacity-50'
                      : ''
                  "
                >
                  <span class="font-primary font-extrabold text-blue-brand text-16 tracking-1/4">{{
                    addon.addon.name
                  }}</span>
                  <span class="font-primary font-normal text-blue-brand text-16 tracking-1/4"
                    >{{ readableDate(addon.start_date) }} - {{ readableDate(addon.end_date) }}</span
                  >
                </span>
              </label>
              <!-- addon_extras -->
              <template v-if="addon.extras.length > 0 && addonIsChecked(addon.addon.slug)">
                <label
                  v-for="(extra, index) in addon.extras"
                  :key="`addon_extra-${index}`"
                  class="w-full my-5 px-7.5 flex items-center"
                  :for="`extra-${extra.extra.uuid}-${guest.uuid}`"
                >
                  <span class="block h-5 w-5 relative cursor-pointer">
                    <input
                      type="checkbox"
                      class="custom-checkbox h-5 w-5 bg-white rounded checked:bg-yellow-brand cursor-pointer"
                      :id="`extra-${extra.extra.uuid}-${guest.uuid}`"
                      :checked="addonExtraIsChecked(extra.extra.uuid)"
                      :disabled="
                        !addonExtraIsChecked(extra.extra.uuid) &&
                          availableAddonExtras[extra.extra.uuid] <=
                            assignedAddonExtras[extra.extra.uuid]
                      "
                      @change="toggleAddonExtraChecked($event, extra)"
                    />
                    <svg-sprite
                      href="icon-tick"
                      class="checkbox-tick h-4 w-4 absolute pin-c pointer-events-none"
                    ></svg-sprite>
                  </span>
                  <span
                    class="pl-2.5 flex flex-col cursor-pointer"
                    :class="
                      !addonExtraIsChecked(extra.extra.uuid) &&
                      availableAddonExtras[extra.extra.uuid] <=
                        assignedAddonExtras[extra.extra.uuid]
                        ? 'opacity-50'
                        : ''
                    "
                  >
                    <span
                      class="font-primary font-extrabold text-blue-brand text-16 tracking-1/4"
                      >{{ extra.extra.name }}</span
                    >
                    <span class="font-primary font-normal text-blue-brand text-16 tracking-1/4"
                      >{{ readableDate(extra.start_date) }} -
                      {{ readableDate(extra.end_date) }}</span
                    >
                  </span>
                </label>
              </template>
            </div>
          </template>
          <template v-else>
            <p class="text-20 leading-1.6 my-5 px-5">You have no addons for this adventure.</p>
          </template>
        </template>
        <template v-else>
          <!-- extras -->
          <template v-if="workingGuest.extras.length > 0">
            <label
              v-for="(extra, index) in workingGuest.extras"
              :key="`complete-extra-${index}`"
              class="relative w-full my-5 px-7.5 flex items-center"
            >
              <span class="block h-5 w-5 relative">
                <input
                  type="checkbox"
                  class="custom-checkbox h-5 w-5 bg-white rounded checked:bg-yellow-brand"
                  :disabled="true"
                  :checked="true"
                />
                <svg-sprite
                  href="icon-tick"
                  class="checkbox-tick h-4 w-4 absolute pin-c pointer-events-none"
                ></svg-sprite>
              </span>
              <span class="pl-2.5 flex flex-col">
                <span class="font-primary font-extrabold text-blue-brand text-16 tracking-1/4">{{
                  getExtraDetails(extra.id).name
                }}</span>
                <span class="font-primary font-normal text-blue-brand text-16 tracking-1/4"
                  >{{ getExtraDetails(extra.id).start }} - {{ getExtraDetails(extra.id).end }}</span
                >
              </span>
            </label>
          </template>
          <template v-else>
            <p class="text-20 leading-1.6 my-5 px-5">You have no extras for this adventure.</p>
          </template>
          <!-- addons -->
          <template v-if="workingGuest.addons.length > 0">
            <div
              v-for="(addon, addon_index) in workingGuest.addons"
              :key="`complete-addon-${addon_index}`"
            >
              <div class="w-full my-5 px-7.5 flex items-center">
                <label class="block h-5 w-5 relative">
                  <input
                    type="checkbox"
                    class="custom-checkbox h-5 w-5 bg-white rounded checked:bg-yellow-brand"
                    :disabled="true"
                    :checked="true"
                  />
                  <svg-sprite
                    href="icon-tick"
                    class="checkbox-tick h-4 w-4 absolute pin-c pointer-events-none"
                  ></svg-sprite>
                </label>
                <div class="pl-2.5 flex flex-col">
                  <span class="font-primary font-extrabold text-blue-brand text-16 tracking-1/4">{{
                    getAddonDetails(addon.id).name
                  }}</span>
                  <span class="font-primary font-normal text-blue-brand text-16 tracking-1/4"
                    >{{ getAddonDetails(addon.id).start }} -
                    {{ getAddonDetails(addon.id).end }}</span
                  >
                </div>
              </div>
              <!-- addon_extras -->
              <template v-if="addon.extras.length > 0">
                <div
                  v-for="(extra, extra_index) in addon.extras"
                  :key="`complete-addon_extra-${extra_index}`"
                  class="w-full my-5 px-7.5 flex items-center"
                >
                  <label class="block h-5 w-5 relative">
                    <input
                      type="checkbox"
                      class="custom-checkbox h-5 w-5 bg-white rounded checked:bg-yellow-brand"
                      :disabled="true"
                      :checked="true"
                    />
                    <svg-sprite
                      href="icon-tick"
                      class="checkbox-tick h-4 w-4 absolute pin-c pointer-events-none"
                    ></svg-sprite>
                  </label>
                  <div class="pl-2.5 flex flex-col">
                    <span
                      class="font-primary font-extrabold text-blue-brand text-16 tracking-1/4"
                      >{{ getAddonExtraDetails(extra.id).name }}</span
                    >
                    <span class="font-primary font-normal text-blue-brand text-16 tracking-1/4"
                      >{{ getAddonExtraDetails(extra.id).start }} -
                      {{ getAddonExtraDetails(extra.id).end }}</span
                    >
                  </div>
                </div>
              </template>
            </div>
          </template>
          <template v-else>
            <p class="text-20 leading-1.6 my-5 px-5">You have no addons for this adventure.</p>
          </template>
        </template>

        <p v-if="!isComplete" class="text-16 leading-1.63 my-5 pl-7.5 pr-5">
          If you have not selected Single Accomdation and would like to share a room with someone
          you have booked with please state their name. If you would like us to pair you up in a
          twin room with another person on the Adventure (of the same gender), then please put “Twin
          with other”.
        </p>

        <!-- TODO If Single accomodation selected don't show - leave for now - don't know if we should do this, there may be more than one single accom option depending on what extras/addons have been chosen, there is also help text below anyway. -->

        <div class="flex flex-col md:flex-row my-5 px-5">
          <div class="relative w-full md:w-1/2 md:pr-2.5 mb-5 md:mb-0">
            <span
              v-if="!workingGuest.rooming_preference && !isComplete"
              class="absolute top-px mt-px -left-3 h-2.5 w-2.5 rounded-full bg-yellow-brand"
            ></span>
            <label for="guestRoomingPreference" class="relative label block mb-2"
              >Rooming Preference</label
            >
            <template v-if="!isComplete">
              <input
                type="text"
                name="guestRoomingPreference"
                class="input block w-full"
                placeholder="Rooming reference"
                @change="onChange"
                v-model="workingGuest.rooming_preference"
              />
            </template>
            <template v-else>
              <p class="text-20 leading-1.6">{{ workingGuest.rooming_preference }}</p>
            </template>
          </div>
          <div class="w-full md:w-1/2 md:pl-2.5"></div>
        </div>

        <div class="relative w-full my-5 px-5">
          <span
            v-if="!workingGuest.dietary_requirements && !isComplete"
            class="absolute top-px left-2 h-2.5 w-2.5 rounded-full bg-yellow-brand"
          ></span>
          <label for="guestDietaryRequirements" class="label block mb-2"
            >Dietary requirements</label
          >
          <template v-if="!isComplete">
            <textarea
              name="guestDietaryRequirements"
              class="input block w-full"
              rows="4"
              placeholder="Any special dietary requirements, if you have none please type N/A…"
              @change="onChange"
              v-model="workingGuest.dietary_requirements"
            ></textarea>
          </template>
          <template v-else>
            <p class="text-20 leading-1.6">{{ workingGuest.dietary_requirements }}</p>
          </template>
        </div>

        <h2
          class="font-primary font-extrabold text-blue-brand text-26 leading-1 -tracking-1/2 mx-5 pt-5 mb-5 border-t border-black-0.1"
        >
          Travel Information
        </h2>

        <div v-if="!isBooker && !isComplete" class="w-full my-5 px-5 flex items-center">
          <label class="block h-5 w-5 relative cursor-pointer">
            <input
              type="checkbox"
              class="custom-checkbox h-5 w-5 bg-white rounded checked:bg-yellow-brand cursor-pointer"
              @click.stop="toggleSameTravelInfoAsBooker($event)"
            />
            <svg-sprite
              href="icon-tick"
              class="checkbox-tick h-4 w-4 absolute pin-c pointer-events-none"
            ></svg-sprite>
          </label>
          <span class="pl-2.5 cursor-pointer">Same Details as Booker</span>
        </div>

        <div class="relative w-full my-5 px-5">
          <span
            v-if="!workingGuest.travel_information && !isComplete"
            class="absolute top-px left-2 h-2.5 w-2.5 rounded-full bg-yellow-brand"
          ></span>
          <label for="guestTravelInformation" class="label block mb-2">Travel Information</label>
          <template v-if="!isComplete">
            <textarea
              name="guestTravelInformation"
              class="input block w-full"
              rows="4"
              placeholder="Full details of your travel arrangements…"
              @change="onChange"
              v-model="workingGuest.travel_information"
            ></textarea>
          </template>
          <template v-else>
            <p class="text-20 leading-1.6">{{ workingGuest.travel_information }}</p>
          </template>
        </div>

        <h2
          class="font-primary font-extrabold text-blue-brand text-26 leading-1 -tracking-1/2 mx-5 pt-5 mb-5 border-t border-black-0.1"
        >
          Aditional Information
        </h2>

        <div class="w-full my-5 px-5">
          <label for="guestNotes" class="label block mb-2">Notes</label>
          <template v-if="!isComplete">
            <textarea
              name="guestNotes"
              class="input block w-full"
              rows="4"
              placeholder="Any special requirements or additional information…"
              @change="onChange"
              v-model="workingGuest.notes"
            ></textarea>
          </template>
          <template v-else>
            <p class="text-20 leading-1.6">{{ workingGuest.notes }}</p>
          </template>
        </div>

        <div v-if="!isComplete" class="w-full my-5 px-5">
          <button type="submit" class="button min-w-280 md:min-w-360" :disabled="submitting">
            {{ submitting ? 'Submitting...' : 'Update' }}
          </button>
        </div>
      </form>
    </div>
    <div v-if="errorMessage" class="text-red-400 my-4">
      {{ errorMessage }}
    </div>

    <!-- Put error messages here otherwise they push the form down and make parts not visible due to expanding form -->
    <all-errors v-if="errors" :errors="errors"></all-errors>
    <all-errors v-if="parentFormErrors" :errors="parentFormErrors"></all-errors>
  </div>
</template>

<script>
import ErrorMessage from './ErrorMessage.vue';
import HandlesErrors from './Mixins/HandlesErrors';
import PhoneMaskInput from 'vue-phone-mask-input';
import SvgSprite from '../vendor/enso/components/SvgSprite.vue';
import debounce from 'lodash/debounce';
import { collapseSection, expandSection, formatDate } from '../src/Helpers.js';
import Route from '../src/Route';
import Datepicker from 'vuejs-datepicker';
import { formatISO } from 'date-fns';
import AllErrors from './AllErrors.vue';

export default {
  components: {
    AllErrors,
    ErrorMessage,
    PhoneMaskInput,
    SvgSprite,
    Datepicker,
  },

  mixins: [HandlesErrors],

  props: {
    isBooker: {
      type: Boolean,
      default: false,
    },
    booker: {
      type: Object,
      required: true,
    },
    guestNumber: {
      type: Number,
      default: 1,
    },
    guest: {
      type: Object,
      required: true,
    },
    extras: {
      type: Array,
      required: true,
    },
    addons: {
      type: Array,
      required: true,
    },
    addonExtras: {
      type: Array,
      required: true,
    },
    assignedAddons: {
      type: Object,
      required: true,
    },
    assignedExtras: {
      type: Object,
      required: true,
    },
    assignedAddonExtras: {
      type: Object,
      required: true,
    },
    availableAddons: {
      type: Object,
      required: true,
    },
    availableExtras: {
      type: Object,
      required: true,
    },
    availableAddonExtras: {
      type: Object,
      required: true,
    },
    passportTypes: {
      type: Object,
      default: () => {},
    },
    startOpen: {
      type: Boolean,
      default: false,
    },
    parentFormErrors: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      isoDOB: this.guest.dob,
      isoExpiry: this.guest.passport_expires,
      workingGuest: this.guest,
      fullInsuranceContactNumber: this.guest.insurance_contact_number,
      fullNextOfKinPhone: this.guest.next_of_kin_phone,
      fullPhoneNumber: this.guest.phone,
      guestsExtras: [],
      guestsAddons: [],
      guestsAddonExtras: [],
      submitting: false,
      isComplete: this.guest.locked,
    };
  },

  watch: {
    fullInsuranceContactNumber: debounce(function() {
      this.onChange();
    }, 500),
    fullNextOfKinPhone: debounce(function() {
      this.onChange();
    }, 500),
    fullPhoneNumber: debounce(function() {
      this.onChange();
    }, 500),
    guestsExtras: {
      handler: debounce(function() {
        this.onChange();
      }, 500),
      deep: true,
    },
    guestsAddons: {
      handler: debounce(function() {
        this.onChange();
      }, 500),
      deep: true,
    },
    guestsAddonExtras: {
      handler: debounce(function() {
        this.onChange();
      }, 500),
      deep: true,
    },
    workingGuest(guest) {
      this.isComplete = guest.locked;
    },
    guest(guest) {
      this.isComplete = guest.locked;
    },
  },

  computed: {
    unfilledFields() {
      let total = 0;

      if (!this.workingGuest.real_name) {
        total = total + 1;
      }
      if (!this.workingGuest.dob) {
        total = total + 1;
      }
      if (!this.workingGuest.phone && !this.workingGuest.email) {
        total = total + 1;
      }
      if (!this.workingGuest.address) {
        total = total + 1;
      }
      if (!this.workingGuest.postcode) {
        total = total + 1;
      }
      if (!this.workingGuest.country) {
        total = total + 1;
      }
      if (!this.workingGuest.passport_type) {
        total = total + 1;
      }
      if (!this.workingGuest.passport_number) {
        total = total + 1;
      }
      if (!this.workingGuest.passport_expires) {
        total = total + 1;
      }
      if (!this.workingGuest.insurance_provider) {
        total = total + 1;
      }
      if (!this.workingGuest.insurance_policy_number) {
        total = total + 1;
      }
      if (!this.workingGuest.insurance_contact_number) {
        total = total + 1;
      }
      if (!this.workingGuest.next_of_kin_name) {
        total = total + 1;
      }
      if (!this.workingGuest.next_of_kin_phone && !this.workingGuest.next_of_kin_email) {
        total = total + 1;
      }
      if (
        this.guestsAddons.length < 1 &&
        this.guestsExtras.length < 1 &&
        this.guestsAddonExtras.length < 1
      ) {
        total = total + 1;
      }
      if (!this.workingGuest.rooming_preference) {
        total = total + 1;
      }
      if (!this.workingGuest.travel_information) {
        total = total + 1;
      }

      if (this.isComplete) {
        return 0;
      } else {
        return total;
      }
    },
  },

  created() {
    this.decompileAddonsExtras();

    this.workingGuest.next_of_kin_preferred_contact = this.guest.next_of_kin_preferred_contact
      ? this.guest.next_of_kin_preferred_contact
      : 'phone';
    this.workingGuest.preferred_contact_method = this.guest.preferred_contact_method
      ? this.guest.preferred_contact_method
      : 'phone';
    this.onChange();
  },

  mounted() {
    if (this.startOpen) {
      this.toggleForm();
    }
  },

  methods: {
    readableDate(date) {
      return formatDate(date);
    },

    updateDOB(date) {
      this.isoDOB = formatISO(date);
    },

    updateExpiry(date) {
      this.isoExpiry = formatISO(date);
    },

    toggleForm() {
      if (!this.$refs.expandButton.classList.contains('is-active')) {
        expandSection(this.$refs.expandForm);
        this.$refs.expandButton.classList.add('is-active');
      } else if (this.$refs.expandButton.classList.contains('is-active')) {
        collapseSection(this.$refs.expandForm);
        this.$refs.expandButton.classList.remove('is-active');
      }
    },

    onUpdatePhoneNumber(payload) {
      if (payload.isValidByLibPhoneNumberJs) {
        this.fullPhoneNumber = payload.number;
      }
    },

    onUpdateInsurancePhoneNumber(payload) {
      if (payload.isValidByLibPhoneNumberJs) {
        this.fullInsuranceContactNumber = payload.number;
      }
    },

    onUpdateNextOfKinPhoneNumber(payload) {
      if (payload.isValidByLibPhoneNumberJs) {
        this.fullNextOfKinPhone = payload.number;
      }
    },

    toggleSameAddressAsBooker(e) {
      if (e.target.checked) {
        this.workingGuest = Object.assign(this.workingGuest, {
          address: this.booker.address,
          country: this.booker.country,
          postcode: this.booker.postcode,
        });
      } else {
        this.workingGuest = Object.assign(this.workingGuest, {
          address: this.guest.address,
          country: this.guest.country,
          postcode: this.guest.postcode,
        });
      }
      this.onChange();
    },

    toggleSameInsuranceAsBooker(e) {
      if (e.target.checked) {
        this.workingGuest = Object.assign(this.workingGuest, {
          insurance_provider: this.booker.insurance_provider,
          insurance_policy_number: this.booker.insurance_policy_number,
          insurance_contact_number: this.booker.insurance_contact_number,
        });
      } else {
        this.workingGuest = Object.assign(this.workingGuest, {
          insurance_provider: this.guest.insurance_provider,
          insurance_policy_number: this.guest.insurance_policy_number,
          insurance_contact_number: this.guest.insurance_contact_number,
        });
      }
      this.onChange();
    },

    toggleSameNextOfKinAsBooker(e) {
      if (e.target.checked) {
        this.workingGuest = Object.assign(this.workingGuest, {
          next_of_kin_email: this.booker.next_of_kin_email,
          next_of_kin_name: this.booker.next_of_kin_name,
          next_of_kin_phone: this.booker.next_of_kin_phone,
          next_of_kin_preferred_contact: this.booker.next_of_kin_preferred_contact,
        });
      } else {
        this.workingGuest = Object.assign(this.workingGuest, {
          next_of_kin_email: this.guest.next_of_kin_email,
          next_of_kin_name: this.guest.next_of_kin_name,
          next_of_kin_phone: this.guest.next_of_kin_phone,
          next_of_kin_preferred_contact: this.guest.next_of_kin_preferred_contact,
        });
      }
      this.onChange();
    },

    toggleSameTravelInfoAsBooker(e) {
      if (e.target.checked) {
        this.workingGuest = Object.assign(this.workingGuest, {
          travel_information: this.booker.travel_information,
        });
      } else {
        this.workingGuest = Object.assign(this.workingGuest, {
          travel_information: this.guest.travel_information,
        });
      }
      this.onChange();
    },

    extraIsChecked(id) {
      return this.guestsExtras.includes(id);
    },

    toggleExtraChecked(event, extra) {
      let exists = this.guestsExtras.includes(extra.extra.uuid);

      if (exists) {
        this.guestsExtras = this.guestsExtras.filter((uuid) => {
          return uuid !== extra.extra.uuid;
        });
      } else {
        this.guestsExtras.push(extra.extra.uuid);
      }
    },

    addonIsChecked(id) {
      return this.guestsAddons.includes(id);
    },

    toggleAddonChecked(event, addon) {
      let exists = this.guestsAddons.includes(addon.addon.slug);

      if (exists) {
        this.guestsAddons = this.guestsAddons.filter((slug) => {
          return slug !== addon.addon.slug;
        });
      } else {
        this.guestsAddons.push(addon.addon.slug);
      }
    },

    addonExtraIsChecked(id) {
      return this.guestsAddonExtras.includes(id);
    },

    toggleAddonExtraChecked(event, extra) {
      let exists = this.guestsAddonExtras.includes(extra.extra.uuid);

      if (exists) {
        this.guestsAddonExtras = this.guestsAddonExtras.filter((uuid) => {
          return uuid !== extra.extra.uuid;
        });
      } else {
        this.guestsAddonExtras.push(extra.extra.uuid);
      }
    },

    // Extract addon/extra data from guest
    decompileAddonsExtras() {
      const addons = [];
      const addonExtras = [];

      this.guest.addons.forEach((addon) => {
        addons.push(addon.id);

        if (addon.extras) {
          addon.extras.forEach((addonExtra) => {
            addonExtras.push(addonExtra.id);
          });
        }
      });

      this.guestsExtras = this.guest.extras.map((extra) => extra.id);

      this.guestsAddons = addons;
      this.guestsAddonExtras = addonExtras;
    },

    // Combine working data into the guest object
    compileAddonsExtras() {
      const output = {};
      output.addons = [];
      output.extras = [];

      this.addons.forEach((availableAddon) => {
        if (!this.guestsAddons.includes(availableAddon.addon.slug)) {
          return;
        }

        const addonData = {
          id: availableAddon.addon.slug,
          quantity: 1,
          extras: [],
        };

        availableAddon.extras.forEach((availableAddonExtra) => {
          if (this.guestsAddonExtras.includes(availableAddonExtra.extra.uuid)) {
            addonData.extras.push({
              id: availableAddonExtra.extra.uuid,
              quantity: 1,
            });
          }
        });

        output.addons.push(addonData);
      });

      this.extras.forEach((availableExtra) => {
        if (!this.guestsExtras.includes(availableExtra.extra.uuid)) {
          return;
        }

        output.extras.push({
          id: availableExtra.extra.uuid,
          quantity: 1,
        });
      });

      return output;
    },

    onChange() {
      let guest = Object.assign(
        this.workingGuest,
        {
          dob: this.isoDOB,
          phone: this.fullPhoneNumber,
          next_of_kin_phone: this.fullNextOfKinPhone,
          insurance_contact_number: this.fullInsuranceContactNumber,
          passport_expires: this.isoExpiry,
        },
        this.compileAddonsExtras()
      );

      this.workingGuest = guest;
      this.$emit('update-guest', guest);
      this.$emit('update-unfilled-fields', {
        id: this.workingGuest.uuid,
        quantity: this.unfilledFields,
      });
    },

    onSubmit() {
      this.submitting = true;
      this.resetErrors();

      axios
        .post(Route('json.guests.update', { guest: this.workingGuest.uuid }), this.workingGuest)
        .then((response) => {
          if (response.status !== 200) {
            throw 'Network error.';
          }
          this.workingGuest = response.data.guest;
          this.isComplete = this.workingGuest.locked;
        })
        .catch((error) => {
          this.handleXHRErrors(error);
        })
        .then(() => {
          this.onChange();
          this.submitting = false;
        });
    },

    getExtraDetails(uuid) {
      let extra = this.extras.find((e) => {
        return e.extra.uuid === uuid;
      });

      if (extra) {
        return {
          name: extra.extra.name,
          start: this.readableDate(extra.start_date),
          end: this.readableDate(extra.end_date),
        };
      } else {
        return {
          name: '',
          start: '',
          end: '',
        };
      }
    },

    getAddonDetails(slug) {
      let addon = this.addons.find((a) => {
        return a.addon.slug === slug;
      });

      if (addon) {
        return {
          name: addon.addon.name,
          start: this.readableDate(addon.start_date),
          end: this.readableDate(addon.end_date),
        };
      } else {
        return {
          name: '',
          start: '',
          end: '',
        };
      }
    },

    getAddonExtraDetails(uuid) {
      let extra = this.addonExtras.find((e) => {
        return e.extra.uuid === uuid;
      });

      if (extra) {
        return {
          name: extra.extra.name,
          start: this.readableDate(extra.start_date),
          end: this.readableDate(extra.end_date),
        };
      } else {
        return {
          name: '',
          start: '',
          end: '',
        };
      }
    },
  },
};
</script>
